import React, {useEffect, useMemo, useRef} from 'react'
import {Divider, makeStyles} from '@material-ui/core'
import {ResMCIV2} from 'api/languageApi'
import HealthCenterReportStyle from 'pages/HealthCenter/ReportV1/Style'
import WrapElement from 'pages/HealthCenter/ReportV1/Elements/WrapElement'
import AIDementiaPredictionGuide from 'pages/HealthCenter/ReportV2/AIDementiaPrediction/AIDementiaPredictionGuide'

const useStyles = makeStyles(HealthCenterReportStyle)

export interface AIDementiaPredictionProps {
  mciResultImage: string
  mciProbability: number
  name: string
  textMCI: ResMCIV2
  footer: string
}

const AIDementiaPredictionV2 = ({
  mciResultImage,
  mciProbability,
  name,
  textMCI,
  footer,
}: AIDementiaPredictionProps) => {
  const classes = useStyles()
  const refs = useRef<HTMLDivElement[]>([])
  const treatmentRef = useRef<HTMLDivElement>(null)
  const formatText = (template: string, values: (string | number)[]) => {
    return template.replace(/{(\d+)}/g, (_, index) => values[+index].toString())
  }

  const data = useMemo(() => {
    const data = textMCI.section.recommendation_table
    return [
      {
        index: 1,
        stepText: `${data.stage1.stage.number} (~40%)`,
        title: data.stage1.stage.description,
        content: data.stage1.recommendations,
        treatment: data.stage1.treatment,
        color: '#43a47b',
        isActive: mciProbability < 40,
      },
      {
        index: 2,
        stepText: `${data.stage2.stage.number} (40~50%)`,
        title: data.stage2.stage.description,
        content: data.stage2.recommendations,
        treatment: data.stage2.treatment,
        color: '#68ae44',
        isActive: mciProbability >= 40 && mciProbability < 50,
      },
      {
        index: 3,
        stepText: `${data.stage3.stage.number} (50~60%)`,
        title: data.stage3.stage.description,
        content: data.stage3.recommendations,
        treatment: data.stage3.treatment,
        color: '#9caf27',
        isActive: mciProbability >= 50 && mciProbability < 60,
      },
      {
        index: 4,
        stepText: `${data.stage4.stage.number} (60~70%)`,
        title: data.stage4.stage.description,
        content: data.stage4.recommendations,
        treatment: data.stage4.treatment,
        color: '#ceb215',
        isActive: mciProbability >= 60 && mciProbability < 70,
      },
      {
        index: 5,
        stepText: `${data.stage5.stage.number} (70~80%)`,
        title: data.stage5.stage.description,
        content: data.stage5.recommendations,
        treatment: data.stage5.treatment,
        color: '#c89331',
        isActive: mciProbability >= 70 && mciProbability < 80,
      },
      {
        index: 6,
        stepText: `${data.stage6.stage.number} (80~85%)`,
        title: data.stage6.stage.description,
        content: data.stage6.recommendations,
        treatment: data.stage6.treatment,
        color: '#be6b29',
        isActive: mciProbability >= 80 && mciProbability < 85,
      },
      {
        index: 7,
        stepText: `${data.stage7.stage.number} (85~90%)`,
        title: data.stage7.stage.description,
        content: data.stage7.recommendations,
        treatment: data.stage7.treatment,
        color: '#b64629',
        isActive: mciProbability >= 85 && mciProbability < 90,
      },
      {
        index: 8,
        stepText: `${data.stage8.stage.number} (90%~)`,
        title: data.stage8.stage.description,
        content: data.stage8.recommendations,
        treatment: data.stage8.treatment,
        color: '#c7302a',
        isActive: mciProbability >= 90,
      },
    ]
  }, [textMCI, mciProbability])

  const currentState = useMemo(() => {
    const activeStage = data.find((item) => item.isActive)
    if (activeStage)
      return textMCI.section.recommendation_table[`stage${activeStage.index}`]
    return {
      recommendations: {
        main: [],
        sub: [],
      },
      treatment: '',
    }
  }, [textMCI.section.recommendation_table])

  useEffect(() => {
    if (textMCI) {
      textMCI.section.recommendation_table.annotation.forEach((item, index) => {
        if (refs.current[index]) {
          refs.current[index].innerHTML = item
        }
      })
    }
  }, [textMCI])

  useEffect(() => {
    if (treatmentRef.current) {
      treatmentRef.current.innerHTML = currentState.treatment || ''
    }
  }, [currentState.treatment])

  return (
    <div className={classes.brainBasic}>
      <WrapElement number={1} text={textMCI.section.prediction.title}>
        <div className={classes.contentCard}>
          <img
            className={classes.mciResultImage}
            src={mciResultImage}
            alt='mci'
          />
        </div>
        <div className={classes.mciResultDesContent}>
          <div className={classes.mciResultDesContentList}>
            <div className={classes.mciResultDesContentText}>
              <span>1</span>
              <div style={{width: '99%'}}>
                {textMCI.section.prediction.opinions.text_box.circle1}
              </div>
            </div>
            <div className={classes.mciResultDesContentText}>
              <span>2</span>
              <div style={{width: '99%'}}>
                {textMCI.section.prediction.opinions.text_box.circle2}
              </div>
            </div>
          </div>
          <div className={classes.mciResultDesContentText}>
            {textMCI.section.prediction.opinions.text_box.explanation}
          </div>
        </div>
        <div className={classes.mciResultDes}>
          <div className={classes.mciResultDesTitle}>
            {formatText(textMCI.section.prediction.opinions.diagnosis, [
              name,
              data.find((item) => item.isActive)?.index || 1,
            ])}
          </div>
          <div className={classes.stageRecommendation}>
            {data.map((stage) => (
              <div className={classes.stageWrapper} key={stage.index}>
                <div className={classes.stageTitle}>
                  <div
                    style={{
                      color: stage.color,
                    }}
                  >
                    {stage.stepText}
                  </div>
                  <div
                    style={{
                      color: stage.isActive ? stage.color : '#d8d9e0',
                    }}
                  >
                    {stage.title.replaceAll('<br>', ' ')}
                  </div>
                </div>
                {stage.isActive && (
                  <div className={classes.stageMainRecommendation}>
                    <ul
                      style={{
                        listStyleType: 'disc',
                        marginLeft: 20,
                      }}
                    >
                      {stage.content.main?.map((text: string) => (
                        <li key={text}>{text}</li>
                      ))}
                    </ul>
                    {stage.content.sub?.map((text: string) => (
                      <div className={classes.subRecommendation} key={text}>
                        {`‒ ${text}`}
                      </div>
                    ))}
                    <div
                      ref={treatmentRef}
                      style={{
                        marginLeft: 20,
                        marginTop: 10,
                      }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </WrapElement>
      <WrapElement number={2} text={textMCI.section.recommendation_table.title}>
        <div className={classes.AIDementiaPredictionDetail}>
          {data.map((content) => (
            <AIDementiaPredictionGuide
              key={content.index}
              stepText={content.stepText}
              color={content.color}
              title={content.title}
              content={content.content}
              isActive={content.isActive}
              treatment={content.treatment}
            />
          ))}
        </div>
        <div className={classes.mciResultNote}>
          {textMCI.section.recommendation_table.annotation.map((_, index) => (
            <div
              className={classes.mciResultNoteElm}
              key={index}
              ref={(el) => {
                if (el) refs.current[index] = el
              }}
            />
          ))}
        </div>
      </WrapElement>
      <Divider style={{marginLeft: 10, marginRight: 10, marginBottom: 10}} />
      <div className={classes.footer}>{footer}</div>
    </div>
  )
}

export default AIDementiaPredictionV2
